import React from "react";
import { Box, Typography, Divider } from "@mui/joy";
import { Ruler, Palette, Shield, PaintBucket, Hash } from "lucide-react";
import { FormSelect, FormInput, FormActionSelect, Textarea } from "../../../shared";

export function GuttersAndDownspouts({ loading }: { loading?: boolean }) {
    return (
        <Box
        >
            <Box mb={2}>
                <Typography level="title-md">Gutters & Downspouts</Typography>
                <Typography level="body-sm" color="neutral">
                    Specify gutter and downspout details, including guards and paint.
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: "rgba(128, 128, 128, 0.2)",
                    bgcolor: "rgba(128, 128, 128, 0.05)",
                    backdropFilter: "blur(12px)",
                }}
            >
                {/* GUTTERS */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    <FormSelect
                        name="GutterSize"
                        label="Gutter Size"
                        placeholder="Select size"
                        helperText="Dimensions of the gutter."
                        disabled={loading}
                        startDecorator={<Ruler size={16} />}
                        options={['5"', '6"']}
                    />

                    <FormInput
                        name="GutterColor"
                        label="Gutter Color"
                        placeholder="e.g. White"
                        helperText="Color of the gutter."
                        disabled={loading}
                        startDecorator={<Palette size={16} />}
                    />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Gutter Length (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="GutterLengthQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="GutterLengthAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5, opacity: 0.6 }} />

                {/* DOWNSPOUTS */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    <FormSelect
                        name="DownspoutSize"
                        label="Downspout Size"
                        placeholder="Select size"
                        helperText="Dimensions of downspout."
                        disabled={loading}
                        startDecorator={<Ruler size={16} />}
                        options={['2x3"', '3x4"']}
                    />

                    <FormInput
                        name="DownspoutColor"
                        label="Downspout Color"
                        placeholder="e.g. White"
                        helperText="Color of the downspout."
                        disabled={loading}
                        startDecorator={<Palette size={16} />}
                    />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Downspout Length (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="DownspoutLengthQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<Hash size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="DownspoutLengthAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5, opacity: 0.6 }} />

                {/* DIRECTIONAL ELBOWS & OFFSET */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)",
                        },
                        gap: 2.5,
                    }}
                >
                    <FormInput
                        name="DirectionalElbowStyleAQty"
                        label="Directional Elbow — Style A"
                        type="number"
                        placeholder="0"
                        helperText="Number of Style A directional elbows."
                        disabled={loading}
                        startDecorator={<Hash size={16} />}
                    />

                    <FormInput
                        name="DirectionalElbowStyleBQty"
                        label="Directional Elbow — Style B"
                        type="number"
                        placeholder="0"
                        helperText="Number of Style B directional elbows."
                        disabled={loading}
                        startDecorator={<Hash size={16} />}
                    />

                    <FormInput
                        name="OffsetQty"
                        label="Offset"
                        type="number"
                        placeholder="0"
                        helperText="Number of gutter offsets."
                        disabled={loading}
                        startDecorator={<Hash size={16} />}
                    />
                </Box>

                <Divider sx={{ my: 2.5, opacity: 0.6 }} />

                {/* ADD-ONS (Guards & Paint) */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(3, 1fr)"
                        },
                        gap: 2.5,
                    }}
                >
                    <FormSelect
                        name="GutterGuardType"
                        label="Gutter Guard Type"
                        placeholder="Select type"
                        helperText="Type of gutter guard."
                        disabled={loading}
                        startDecorator={<Shield size={16} />}
                        options={["None", "Standard", "High Grade"]}
                    />

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                        <Typography level="body-xs" sx={{ textTransform: "uppercase", fontWeight: "xl", letterSpacing: "0.5px" }}>
                            Paint Gutter (LF)
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ mt: -0.5, mb: 0.5, fontSize: "12px" }}>
                            Length and action.
                        </Typography>
                        <Box sx={{ display: "flex", gap: 0, alignItems: "flex-start", minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <FormInput
                                    name="PaintGutterQty"
                                    type="number"
                                    placeholder="0"
                                    sx={{
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        borderRight: 0,
                                        minWidth: 0,
                                    }}
                                    disabled={loading}
                                    startDecorator={<PaintBucket size={16} />}
                                />
                            </Box>
                            <Box sx={{ width: 100, flexShrink: 0 }}>
                                <FormActionSelect
                                    name="PaintGutterAction"
                                    disabled={loading}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5, opacity: 0.6 }} />

                <Textarea
                    name="GutterNotes"
                    label="Gutter Notes"
                    placeholder="Additional gutter or downspout details"
                    helperText="Optional notes for the gutter scope."
                    disabled={loading}
                    minRows={3}
                    sx={{
                        bgcolor: "background.surface",
                        borderRadius: "4px",
                        boxShadow: "none",
                    }}
                />
            </Box>
        </Box>
    );
}
